import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  useTheme,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import {
  EmojiEvents,
  CheckCircle,
  Cancel,
  Refresh,
  ArrowBack,
  Assignment,
  AccessTime
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { ExamAttempt, Course } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const ExamResult: React.FC = () => {
  const theme = useTheme();
  const { courseId, examId, attemptId } = useParams<{ courseId: string; examId: string; attemptId: string }>();
  const navigate = useNavigate();

  // Used to prevent stale async polling responses from updating the UI
  const pollRequestIdRef = useRef(0);
  
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [certificateStatus, setCertificateStatus] = useState<'pending' | 'generating' | 'ready' | 'failed' | ''>('');
  const [certificateUrl, setCertificateUrl] = useState<string>('');
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState<string>('');
  const [certificatePolling, setCertificatePolling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [certificateError, setCertificateError] = useState<string>('');

  useEffect(() => {
    const fetchResult = async () => {
      if (!attemptId || !courseId) return;
      
      try {
        setLoading(true);
        // Fetch attempt data
        const attemptData = await apiService.getExamAttempt(parseInt(attemptId));
        setAttempt(attemptData);

        // Fetch course info
        const { results: allCourses } = await apiService.getPublishedCourses(1, 500);
        const courseData = allCourses.find(c => c.hashed === courseId);
        setCourse(courseData || null);
      } catch (error) {
        console.error('Error fetching result:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [attemptId, courseId]);

  // Poll certificate status until generated or failed
  useEffect(() => {
    if (
      !attempt ||
      !attempt.is_passed ||
      certificatePolling ||
      certificateStatus === 'ready' ||
      certificateStatus === 'failed'
    ) {
      return;
    }
    const delayMs = 3000;

    pollRequestIdRef.current += 1;
    const myPollId = pollRequestIdRef.current;

    const fetchStatus = async () => {
      if (!attempt) return;
      try {
        const data = await apiService.getExamAttemptCertificateStatus(attempt.id);
        if (myPollId !== pollRequestIdRef.current) return;

        const statusVal = (data.status as string) || 'pending';
        setCertificateStatus(statusVal as any);
        setCertificateError((data.error_message as string) || '');
        setCertificateUrl(data.certificate_url || '');
        setCertificatePreviewUrl(data.certificate_preview_url || '');

        if (statusVal === 'ready' || statusVal === 'failed') {
          setCertificatePolling(false);
          return;
        }

        // Keep polling until the backend reports ready/failed.
        setTimeout(fetchStatus, delayMs);
      } catch (err: unknown) {
        if (myPollId !== pollRequestIdRef.current) return;
        // Must set 'failed' explicitly — (prev || 'failed') kept 'generating' and retriggered this effect forever.
        setCertificateStatus('failed');
        const ax = err as { response?: { status?: number; data?: { detail?: string } } };
        const msg =
          ax.response?.status === 401 || ax.response?.status === 403
            ? 'Sign in required to load certificate status. Refresh the page after logging in.'
            : (ax.response?.data?.detail as string) || 'Could not load certificate status.';
        setCertificateError(msg);
        setCertificatePolling(false);
      }
    };

    setCertificatePolling(true);
    // Show immediate feedback while we poll.
    setCertificateStatus('generating');
    setCertificateError('');
    fetchStatus();
  }, [attempt, certificatePolling, certificateStatus]);

  if (loading) return <LoadingSpinner message="Calculating your results..." />;
  if (!attempt) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.5)',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            border: '2px solid #ef4444',
          }}
        >
          <Cancel sx={{ fontSize: '3.5rem', color: '#fecaca', mb: 2 }} />
  
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 1.5,
              color: '#fecaca',
              textShadow: '0 2px 10px rgba(0,0,0,0.4)',
            }}
          >
            Attempt not found
          </Typography>
  
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: '#e5e7eb',
            }}
          >
            We couldn’t find this exam attempt. It may have expired, been removed, or the link might be incorrect.
          </Typography>
  
          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/courses/${courseId}`)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.25,
              fontSize: '0.95rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              color: '#022c22',
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              },
            }}
          >
            Back to Course
          </Button>
        </Paper>
      </Container>
    );
  }

  const isPassed = attempt.is_passed;

  const handlePreviewCertificate = async () => {
    if (!attempt) return;
    // If backend already exposed a direct preview URL, use it
    if (certificatePreviewUrl) {
      window.open(certificatePreviewUrl, '_blank');
      return;
    }
    // Open a tab immediately so popup blockers treat this as a user gesture
    const win = window.open('', '_blank');
    if (!win) {
      return;
    }
    win.document.write('<p style="font-family:sans-serif;padding:16px;">Loading certificate preview...</p>');
    win.document.close();

    apiService
      .getCertificatePreviewCourse(attempt.id)
      .then((data) => {
        if (data.image_url) {
          win.location.href = data.image_url;
        } else if (data.html) {
          win.document.open();
          win.document.write(data.html);
          win.document.close();
        } else {
          win.document.body.innerHTML =
            '<p style="font-family:sans-serif;padding:16px;">Preview not available.</p>';
        }
      })
      .catch((error) => {
        console.error('Error loading certificate preview', error);
        win.document.body.innerHTML =
          '<p style="font-family:sans-serif;padding:16px;color:red;">Failed to load certificate preview.</p>';
      });
  };

  const handleDownloadCertificate = async () => {
    if (!attempt || !course) return;

    // If backend exposed a direct certificate URL and we don't need a custom filename, use it
    if (certificateUrl) {
      window.open(certificateUrl, '_blank');
      return;
    }

    try {
      setDownloading(true);
      const safeName = course.name.replace(/\s+/g, '_').slice(0, 50);
      await apiService.downloadCertificateCourse(attempt.id, `certificate_${safeName}.pdf`);
    } catch (error) {
      console.error('Error downloading certificate', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendCertificateEmail = async () => {
    if (!attempt) return;
    try {
      setSendingEmail(true);
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const url = `${apiBase.replace(/\/$/, '')}/certificates/course/${attempt.id}/send-email/`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const msg = (data && (data.detail as string)) || `Failed with status ${response.status}`;
        alert(`Failed to send certificate email: ${msg}`);
        return;
      }
      alert('Certificate has been sent to your email.');
    } catch (err) {
      console.error('Error sending certificate email', err);
      alert('Unexpected error while sending certificate email.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {course && (
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'Courses', path: '/courses' },
            { label: course.name, path: `/courses/${course.hashed}` },
            { label: attempt.exam.title, path: `/courses/${course.hashed}/exams/${attempt.exam.hashed}` },
            { label: 'Result' }
          ]}
        />
      )}

      <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
        <Paper 
          sx={{ 
            p: { xs: 4, md: 6 }, 
            borderRadius: 4, 
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            background: isPassed 
              ? 'linear-gradient(135deg, #1e293b 0%, #0f766e 100%)'
              : 'linear-gradient(135deg, #1e293b 0%, #7f1d1d 100%)',
            overflow: 'hidden',
            position: 'relative',
            border: `3px solid ${isPassed ? '#10b981' : '#ef4444'}`,
            maxWidth: '700px',
            width: '100%',
          }}
        >
          {/* Background decoration */}
          <Box sx={{ 
            position: 'absolute', 
            top: -100, 
            right: -100, 
            width: 250, 
            height: 250, 
            borderRadius: '50%', 
            background: isPassed 
              ? 'radial-gradient(circle, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0) 70%)'
              : 'radial-gradient(circle, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0) 70%)',
            zIndex: 0
          }} />
          
          <Box sx={{ 
            position: 'absolute', 
            bottom: -80, 
            left: -80, 
            width: 200, 
            height: 200, 
            borderRadius: '50%', 
            background: isPassed 
              ? 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0) 70%)'
              : 'radial-gradient(circle, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0) 70%)',
            zIndex: 0
          }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Trophy/Icon */}
            <Box 
              sx={{ 
                display: 'inline-flex',
                p: 2.5,
                borderRadius: '50%',
                background: isPassed 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.15) 100%)',
                border: `3px solid ${isPassed ? '#10b981' : '#ef4444'}`,
                mb: 3,
                boxShadow: isPassed 
                  ? '0 8px 30px rgba(16, 185, 129, 0.5)'
                  : '0 8px 30px rgba(239, 68, 68, 0.5)'
              }}
            >
              {isPassed ? (
                <EmojiEvents sx={{ fontSize: '4rem', color: '#fbbf24' }} />
              ) : (
                <Cancel sx={{ fontSize: '4rem', color: '#fca5a5' }} />
              )}
            </Box>

            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                mb: 2, 
                color: isPassed ? '#6ee7b7' : '#fca5a5',
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.3)'
              }}
            >
              {isPassed ? 'Congratulations!' : 'Keep Trying!'}
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: '#cbd5e1',
                fontWeight: 500,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                px: { xs: 1, md: 4 }
              }}
            >
              {isPassed 
                ? `You have successfully passed the ${attempt.exam.title}!` 
                : `You didn't reach the passing score for the ${attempt.exam.title}.`}
            </Typography>

            {/* Score Display */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: { xs: 3, md: 4 }, 
                mb: 4,
                flexWrap: 'wrap'
              }}
            >
              <Box 
                sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: isPassed 
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0.15) 100%)',
                  minWidth: { xs: '130px', md: '160px' },
                  border: `3px solid ${isPassed ? '#10b981' : '#ef4444'}`,
                  boxShadow: isPassed 
                    ? '0 8px 30px rgba(16, 185, 129, 0.4)'
                    : '0 8px 30px rgba(239, 68, 68, 0.4)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    color: isPassed ? '#6ee7b7' : '#fca5a5',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    lineHeight: 1,
                    textShadow: '0 2px 10px rgba(0,0,0,0.5)'
                  }}
                >
                  {attempt.score}%
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isPassed ? '#a7f3d0' : '#fecaca',
                    fontWeight: 700,
                    mt: 1,
                    fontSize: '0.9rem'
                  }}
                >
                  Your Score
                </Typography>
              </Box>
              
              <Box sx={{ 
                width: 2, 
                height: { xs: 70, md: 80 }, 
                background: 'linear-gradient(to bottom, rgba(203, 213, 225, 0.5), rgba(203, 213, 225, 0.1))',
                borderRadius: 2,
                alignSelf: 'center'
              }} />
              
              <Box 
                sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.2) 0%, rgba(148, 163, 184, 0.1) 100%)',
                  minWidth: { xs: '130px', md: '160px' },
                  border: '3px solid #64748b',
                  boxShadow: '0 8px 30px rgba(100, 116, 139, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    color: '#cbd5e1',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    lineHeight: 1,
                    textShadow: '0 2px 10px rgba(0,0,0,0.5)'
                  }}
                >
                  {attempt.exam.passing_score}%
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#94a3b8',
                    fontWeight: 700,
                    mt: 1,
                    fontSize: '0.9rem'
                  }}
                >
                  Required
                </Typography>
              </Box>
            </Box>

            {/* Status Alert */}
            <Alert 
              severity={isPassed ? "success" : "error"} 
              icon={isPassed ? <CheckCircle /> : <Cancel />}
              sx={{ 
                mb: 4, 
                borderRadius: 2, 
                fontSize: '1rem',
                fontWeight: 700,
                justifyContent: 'center',
                py: 1.5,
                px: 3,
                background: isPassed 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0.15) 100%)',
                color: isPassed ? '#a7f3d0' : '#fecaca',
                border: `2px solid ${isPassed ? '#10b981' : '#ef4444'}`,
                backdropFilter: 'blur(10px)',
                '& .MuiAlert-icon': { 
                  color: isPassed ? '#6ee7b7' : '#fca5a5',
                  fontSize: '1.5rem'
                }
              }}
            >
              {isPassed ? 'PASSED ✓' : 'NOT PASSED ✗'}
            </Alert>

            {isPassed && certificateStatus && (
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: '#e5e7eb',
                  fontWeight: 500,
                }}
              >
                {certificateStatus === 'ready' && certificateUrl
                  ? 'Your certificate is ready.'
                  : certificateStatus === 'failed'
                  ? certificateError || 'We could not generate your certificate automatically. Please contact support.'
                  : 'Preparing your certificate. This may take a few seconds...'}
              </Typography>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate(`/courses/${courseId}`)}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1.25,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderWidth: 2,
                  borderColor: '#94a3b8',
                  color: '#cbd5e1',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { 
                    backgroundColor: 'rgba(148, 163, 184, 0.2)',
                    borderWidth: 2,
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                Back to Course
              </Button>
              
              {!isPassed && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Refresh />}
                  onClick={() => navigate(`/courses/${courseId}/exams/${attempt.exam.hashed}`)}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1.25,
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    color: '#064e3b',
                    boxShadow: '0 6px 25px rgba(16, 185, 129, 0.5)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      boxShadow: '0 8px 30px rgba(16, 185, 129, 0.6)',
                    }
                  }}
                >
                  Try Again
                </Button>
              )}

              {isPassed && certificateStatus && (certificateStatus === 'ready' || certificateStatus === 'failed') && (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Assignment />}
                    onClick={() => navigate(`/courses/${courseId}/exams/${attempt.exam.hashed}`)}
                    sx={{ 
                      borderRadius: 2, 
                      px: 3, 
                      py: 1.25,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      color: '#064e3b',
                      boxShadow: '0 6px 25px rgba(16, 185, 129, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        boxShadow: '0 8px 30px rgba(16, 185, 129, 0.6)',
                      }
                    }}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handlePreviewCertificate}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.25,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      borderWidth: 2,
                      borderColor: '#10b981',
                      color: '#bbf7d0',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        borderColor: '#22c55e',
                      },
                    }}
                  >
                    Preview Certificate
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={downloading}
                    onClick={handleDownloadCertificate}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.25,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      color: '#064e3b',
                      boxShadow: '0 6px 25px rgba(16, 185, 129, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        boxShadow: '0 8px 30px rgba(16, 185, 129, 0.6)',
                      }
                    }}
                  >
                    {downloading ? 'Downloading...' : 'Download Certificate'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ mt: 5, textAlign: 'center' }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#94a3b8',
            fontStyle: 'italic',
            px: { xs: 2, md: 0 },
            fontSize: '0.9rem'
          }}
        >
          📝 Note: For security reasons, correct answers are not displayed. 
          Review the course material and try again if needed.
        </Typography>
      </Box>
    </Container>
  );
};

export default ExamResult;
